/**
 * Triage, step 3 (design 5.2): a message that needs no reply. A pure acknowledgement ("thanks", "ok", "got it")
 * is recognised by a rule (cheap, no model) or by the `no_reply_needed` decision question (Jev, then the LLM). The
 * brain then stays silent, but only when this guard agrees: a real question must never be dropped, so anything
 * that could be a question, a request, a problem or an answer to something we asked gets a reply as usual.
 */

/** The tag the `no_reply_needed` question sets when it fires (core/platform.ts). */
export const NO_REPLY_TAG = 'no_reply_needed'

const ACK_TOKEN = [
  'ok(?:ay)?', 'k', 'kk', 'yep', 'yup', 'got it', 'noted', 'understood', 'will do', 'sounds good', 'all good', 'no worries', 'no problem',
  'thanks?(?: (?:you|u))?(?: (?:so|very) much)?(?: heaps| a lot| again| mate| all)?', 'thank you(?: (?:so|very) much)?(?: again)?', 'many thanks', 'much appreciated', 'appreciated?',
  'thx', 'ty', 'ta', 'cheers', 'great', 'perfect', 'awesome', 'brilliant', 'lovely', 'cool', 'excellent', 'wonderful', 'fantastic', 'amazing', 'nice',
  'see you(?: then| soon| there| on \\w+)?', 'have a (?:good|great|nice) (?:day|one|weekend)', 'merci(?: beaucoup)?', 'gracias', 'danke(?: schön)?', 'grazie',
].join('|')
const SEP = "[^a-z0-9?\\n]*"
/**
 * A message that is only acknowledgement words (one to four of them, any punctuation or emoji), optionally followed
 * by a short sign-off line or two such as a name. Used as the question's rule; `noReplyGuard` still has the last word.
 */
export const ACKNOWLEDGEMENT = `^\\s*${SEP}(?:${ACK_TOKEN})(?:${SEP}(?:${ACK_TOKEN})){0,3}${SEP}(?:\\n+\\s*[-–—~]*\\s*[a-z.'’-]{1,20}(?:[ ,]+[a-z.'’-]{1,20}){0,2}\\s*){0,2}$`

/** Words that turn "thanks" into something we must answer: a request, a question without a mark, a problem. */
const NEEDS_ANSWER = /\b(can|could|would|will you|please|pls|plz|need|needs|want|wants|when|where|how|what|why|which|who|whose|help|issue|problem|wrong|broken|refund|cancel|change|move|book|booking|send|call|ring|phone|email|text|confirm|but|however|also|though|still|not|n't|no longer|haven't|didn't|isn't|wasn't|urgent|asap|invoice|pay|paid|charge|complain|unhappy|disappointed|if)\b/i
/** Closing pleasantries on our side that are not real questions. */
const PLEASANTRY = /\b(anything else|any other questions|let (?:me|us) know if|hope (?:this|that) helps|is there more)\b/i

export interface NoReplyInput {
  text: string
  /** Our last message on this conversation (AI, staff or agent), if any. */
  last_outbound?: string | null
  attachments?: number
  /** An AI reply to an earlier message was still waiting to go and has just been superseded. */
  superseded_reply?: boolean
}

/**
 * Null when staying silent is safe; otherwise the reason a reply is still needed. Fails safe: when in doubt it
 * returns a reason, so the message is answered as usual.
 */
export function noReplyGuard(i: NoReplyInput): string | null {
  const text = String(i.text ?? '').trim()
  if (!text) return 'The message is empty, so it is not an acknowledgement'
  if (text.includes('?')) return 'The message has a question mark'
  if (text.length > 280) return 'The message is longer than a plain acknowledgement'
  if (NEEDS_ANSWER.test(text)) return 'The message has words that may ask for something'
  if ((i.attachments ?? 0) > 0) return 'The customer sent a file or photo'
  if (i.superseded_reply) return 'A reply to an earlier message had not gone yet'
  const ours = String(i.last_outbound ?? '')
  // "ok" to "Shall I move you to Saturday?" is an answer, not an acknowledgement.
  const asked = ours.split(/(?<=[.!?])\s+|\n+/).filter((s) => s.includes('?') && !PLEASANTRY.test(s))
  if (asked.length) return 'Our last message asked the customer something, so this may be their answer'
  return null
}
